import React, { useState, useEffect, useMemo } from 'react';
import { Box, Select, MenuItem, FormControl, InputLabel, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, IconButton, CircularProgress, Alert, List, ListItem, ListItemText, Chip, LinearProgress, Divider, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Collapse, Checkbox, TextField } from '@mui/material';
import { UploadFile, Download, Delete, Refresh, Warning, PlaylistAdd, ExpandMore, ExpandLess, DeleteSweep } from '@mui/icons-material';
import apiService from '../../services/api';
import { supabase } from '../../services/supabase';
import mockAreas from '../../mock/areas.json';
import * as tus from 'tus-js-client';
import QueueManager from './QueueManager';
import TaskHistory from './TaskHistory';
import ReportGenConfigEditor from './ReportGenConfigEditor';
import { createLogger, adminLogContext } from '../../utils/logger'
import { isSessionExpired, isSessionExpiredError, subscribeSessionExpired } from '../../utils/sessionAuth'
import { addUserActionBreadcrumb } from '../../utils/sentryTracking'

const logger = createLogger('FileAndReportManager')
const ADMIN_TAB = 'file_and_report_management'


const StatusChip = ({ label, count, color }) => {
    // Hide chips with 0 count
    if (!count || count === 0) return null;
    return <Chip label={`${count} ${label}`} color={color} size="small" sx={{ mr: 0.5 }} />;
};

const FileAndReportManager = () => {
  const [selectedCounty, setSelectedCounty] = useState('');
  const [displayCounties, setDisplayCounties] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [queueTasks, setQueueTasks] = useState([]);
  const [historyTasks, setHistoryTasks] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [isFileViewerExpanded, setIsFileViewerExpanded] = useState(true);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [currentBatch, setCurrentBatch] = useState([]);
  const [isDocumentsExpanded, setIsDocumentsExpanded] = useState(false);
  const [isConfigExpanded, setIsConfigExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(isSessionExpired());
  const [isGenerating, setIsGenerating] = useState(false);
  const [showForceRerunDialog, setShowForceRerunDialog] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [quickSelectInput, setQuickSelectInput] = useState('5');
  const quickSelectCount = quickSelectInput === '' ? 0 : Math.max(0, parseInt(quickSelectInput, 10) || 0);

  const countyLogCtx = (extra = {}) => {
    const countyData = displayCounties.find((c) => c.full_name === selectedCounty);
    return adminLogContext(ADMIN_TAB, {
      county_name: selectedCounty || undefined,
      county_id: countyData?.county_id ? String(countyData.county_id) : undefined,
      ...extra,
    });
  };

  const fetchCountySpecificData = async ({ silent = false } = {}) => {
    if (!selectedCounty) return;
    if (!silent) setIsLoading(true);
    setError('');
    try {
      const countyData = displayCounties.find(c => c.full_name === selectedCounty);
      if (!countyData || !countyData.county_id) {
        setDocuments([]);
        return;
      };
      const countyId = countyData.county_id;

      const fetchedDocs = await apiService.getCountyDocuments(countyId);
      const byStatus = fetchedDocs.reduce((acc, doc) => {
        const s = doc.processing_status || 'unknown';
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      }, {});
      if (!silent) {
        logger.info('County documents loaded', countyLogCtx({
          action: 'fetch_county_documents',
          document_count: fetchedDocs.length,
          by_status: byStatus,
        }));
      }
      setDocuments(fetchedDocs);
    } catch (err) {
      setError('Failed to fetch county documents.');
      logger.apiFailure('Failed to fetch county documents', countyLogCtx({ action: 'fetch_county_documents' }), err);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const fetchQueueAndHistory = async () => {
    const t0 = performance.now();
    try {
        const [fetchedQueueTasks, fetchedHistoryTasks] = await Promise.all([
            apiService.getQueue(),
            apiService.getQueueHistory()
        ]);
        setQueueTasks(fetchedQueueTasks);
        setHistoryTasks(fetchedHistoryTasks);
        logger.info('Queue and history loaded', adminLogContext(ADMIN_TAB, {
          action: 'fetch_queue',
          queue_count: Array.isArray(fetchedQueueTasks) ? fetchedQueueTasks.length : 0,
          history_count: Array.isArray(fetchedHistoryTasks) ? fetchedHistoryTasks.length : 0,
          load_time_ms: Math.round(performance.now() - t0),
        }));
    } catch (err) {
        setError('Failed to fetch queue data. It may be a moment while the server restarts.');
        logger.apiFailure('Failed to fetch queue data', adminLogContext(ADMIN_TAB, { action: 'fetch_queue' }), err);
    }
  };
  
  const fetchCountyStats = async () => {
    try {
      // County-based stats from backend: one row per county with document counts
      const stats = await apiService.getCountyDocumentStatus();

      const enrichedCounties = (stats || []).map((county) => ({
        name: county.name,
        full_name: county.full_name || `${county.name}, ${county.state}`,
        county_id: county.county_id,
        unprocessed_count: county.unprocessed_count || 0,
        queued_count: county.queued_count || 0,
        processing_count: county.processing_count || 0,
        processed_count: county.processed_count || 0,
        failed_count: county.failed_count || 0,
        skipped_count: county.skipped_count || 0,
        compression_failed_count: county.compression_failed_count || 0,
      }));

      setDisplayCounties(enrichedCounties);
      logger.info('County document stats loaded', adminLogContext(ADMIN_TAB, {
        action: 'fetch_county_stats',
        county_count: enrichedCounties.length,
      }));
    } catch (err) {
      setError('Failed to fetch county data.');
      logger.apiFailure('Failed to fetch county document stats', adminLogContext(ADMIN_TAB, { action: 'fetch_county_stats' }), err);
    }
  }

  useEffect(() => {
    fetchCountyStats();
  }, []);

  useEffect(() => {
    return subscribeSessionExpired(() => setSessionExpired(true));
  }, []);

  useEffect(() => {
    if (!selectedCounty) {
      setDocuments([]);
      return;
    }
    fetchCountySpecificData();
  }, [selectedCounty]);

  // Queue + history are global — always poll, even with no county selected.
  useEffect(() => {
    if (sessionExpired) return;

    const pollQueueAndStats = async () => {
      if (isSessionExpired()) return;
      try {
        const [fetchedQueueTasks, fetchedHistoryTasks] = await Promise.all([
          apiService.getQueue(),
          apiService.getQueueHistory(),
        ]);
        setQueueTasks(fetchedQueueTasks);
        setHistoryTasks(fetchedHistoryTasks);
        await fetchCountyStats();
        return fetchedQueueTasks;
      } catch (err) {
        if (isSessionExpiredError(err)) return [];
        setError('Failed to fetch queue data. It may be a moment while the server restarts.');
        logger.apiFailure('Failed to poll queue data', adminLogContext(ADMIN_TAB, { action: 'poll_queue' }), err);
        return [];
      }
    };

    pollQueueAndStats();
    const interval = setInterval(pollQueueAndStats, 10000);
    return () => clearInterval(interval);
  }, [sessionExpired]);

  // County document list: only re-fetch on poll when expanded or work is active.
  useEffect(() => {
    if (!selectedCounty || sessionExpired) return;

    const pollCountyDocs = async () => {
      if (isSessionExpired()) return;
      try {
        const fetchedQueueTasks = await apiService.getQueue();
        const hasActiveWork = fetchedQueueTasks.some((t) =>
          ['running', 'processing', 'queued'].includes(t.status)
        );
        if (isDocumentsExpanded || hasActiveWork) {
          await fetchCountySpecificData({ silent: true });
        }
      } catch (err) {
        if (isSessionExpiredError(err)) return;
        logger.apiFailure('Failed to poll county documents', countyLogCtx({ action: 'poll_county_documents' }), err);
      }
    };

    pollCountyDocs();
    const interval = setInterval(pollCountyDocs, 10000);
    return () => clearInterval(interval);
  }, [selectedCounty, isDocumentsExpanded, sessionExpired]);


  const getCountyId = async (countyNameStr) => {
    const countyData = displayCounties.find(c => c.full_name === countyNameStr);
    if (!countyData || !countyData.county_id) throw new Error(`Could not find a matching county ID for "${countyNameStr}" in the data. It may not exist in the database yet.`);
    return countyData.county_id;
  };

  const handleFileChange = (event) => {
    if (event.target.files) {
      setSelectedFiles(Array.from(event.target.files));
    }
  };

  const handleRemoveSelectedFile = (indexToRemove) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const runUploads = async (filesToUpload) => {
    setIsUploading(true);
    try {
    const countyId = await getCountyId(selectedCounty);
    const supabaseProjectId = supabase.supabaseUrl.split('.')[0].split('//')[1];

    // Note: We will fetch a fresh access token per-file to avoid token expiry during long batches

    const uploadFile = async (file) => {
      const documentId = crypto.randomUUID();
      const filePath = `${countyId}/${documentId}`;
      
      // Get a fresh token right before starting the upload for this file
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseAccessToken = session?.access_token;
      if (!supabaseAccessToken) {
        return Promise.reject(new Error('No active session. Please log in again.'));
      }

      return new Promise((resolve, reject) => {
        const upload = new tus.Upload(file, {
          endpoint: `${supabase.supabaseUrl}/storage/v1/upload/resumable`,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          headers: {
            Authorization: `Bearer ${supabaseAccessToken}`,
            'x-upsert': 'true',
          },
          uploadDataDuringCreation: true,
          removeFingerprintOnSuccess: true,
          metadata: {
            // Documents bucket; each object lives under a folder named by county_id
            bucketName: 'documents',
            objectName: filePath,
            contentType: file.type,
            cacheControl: '3600',
          },
          // Larger chunks reduce per-request overhead for big batches
          chunkSize: 16 * 1024 * 1024,
          onError: (error) => {
            logger.error('Failed to upload file', countyLogCtx({
              action: 'upload_file',
              file_name: file.name,
              file_path: filePath,
            }), error);
            setUploadProgress(prev => ({ ...prev, [file.name]: { status: 'Failed', error: error.message } }));
            reject(error);
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: { status: `Uploading... ${percentage}%`, progress: parseFloat(percentage) }
            }));
          },
          onSuccess: async () => {
            try {
              setUploadProgress(prev => ({ ...prev, [file.name]: { status: 'Finalizing...', progress: 100 } }));
              
              logger.debug('Upload succeeded, creating DB record', countyLogCtx({
                action: 'upload_finalize',
                file_name: file.name,
                file_path: filePath,
              }));

              await apiService.createDocumentRecord({
                county_id: countyId,
                file_name: file.name,
                file_path: filePath,
                file_size: file.size,
              });
              setUploadProgress(prev => ({ ...prev, [file.name]: { status: 'Completed', progress: 100 } }));
              resolve();
            } catch (apiError) {
              logger.apiFailure('Failed to finalize upload', countyLogCtx({
                action: 'upload_finalize',
                file_name: file.name,
                file_path: filePath,
              }), apiError);
              setUploadProgress(prev => ({ ...prev, [file.name]: { status: 'Failed', error: 'API finalization failed.' } }));
              reject(apiError);
            } finally {
               setCompletedCount(prev => prev + 1);
            }
          },
        });

        upload.findPreviousUploads().then((previousUploads) => {
          if (previousUploads.length) {
            upload.resumeFromPreviousUpload(previousUploads[0]);
          }
          upload.start();
        });
      });
    };

    // Moderate concurrency to avoid storage throttling on large batches
    const CONCURRENCY_LIMIT = 6;
    const fileQueue = [...filesToUpload];

    const workers = Array(CONCURRENCY_LIMIT).fill(null).map(async () => {
      while (fileQueue.length > 0) {
        const file = fileQueue.shift();
        if (file) {
          try {
            await uploadFile(file);
          } catch (e) {
            // Errors are handled in the onError callback, but we catch here to prevent unhandled promise rejections
             setCompletedCount(prev => prev + 1); // Still count it as "processed" for overall progress
          }
        }
      }
    });

    await Promise.all(workers);
    } catch (err) {
      setError(`Upload batch failed: ${err.message}`);
      logger.error('Upload batch failed', countyLogCtx({
        action: 'upload_batch',
        file_count: filesToUpload.length,
      }), err);
    } finally {
    setIsUploading(false);
    fetchCountySpecificData();
    fetchCountyStats();
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !selectedCounty) return;
    
    setCurrentBatch(selectedFiles);
    setTotalFiles(selectedFiles.length);
    setCompletedCount(0);
    setUploadProgress({});
    setOverallProgress(0);
    setIsFileViewerExpanded(false);
    
    runUploads(selectedFiles);
    
    setSelectedFiles([]);
    document.getElementById('file-upload-input').value = "";
  };

  const handleRetryFailed = () => {
    const failedFiles = currentBatch.filter(f => uploadProgress[f.name]?.status === 'Failed');
    if (failedFiles.length === 0) return;

    // Reset progress for the files we are retrying
    const newProgress = { ...uploadProgress };
    failedFiles.forEach(file => {
      delete newProgress[file.name];
    });
    setUploadProgress(newProgress);
    
    setTotalFiles(failedFiles.length);
    setCompletedCount(0);
    setOverallProgress(0);

    runUploads(failedFiles);
  };
  
  const handleClearBatch = () => {
    setCurrentBatch([]);
    setUploadProgress({});
    setTotalFiles(0);
    setCompletedCount(0);
    setOverallProgress(0);
    setIsFileViewerExpanded(true);
  };

  useEffect(() => {
    if (totalFiles > 0) {
      const newProgress = Math.round((completedCount / totalFiles) * 100);
      setOverallProgress(newProgress);
    }
  }, [completedCount, totalFiles]);

  const handleDelete = async (docId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      setIsLoading(true);
      setError('');
      try {
        await apiService.deleteCountyDocument(docId);
        fetchCountySpecificData();
        fetchCountyStats(); // Refresh the counts
      } catch (err) {
        setError('Failed to delete document.');
        logger.apiFailure('Failed to delete document', countyLogCtx({ action: 'delete_document', document_id: String(docId) }), err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDownload = async (filePath, fileName) => {
    try {
      // Create a signed URL that's valid for 1 hour
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 3600);
      
      if (error) throw error;
      if (!data?.signedUrl) throw new Error('Failed to generate download URL');
      
      // Download the file using the signed URL
      const response = await fetch(data.signedUrl);
      if (!response.ok) throw new Error('Failed to fetch file');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Failed to download file', countyLogCtx({
        action: 'download_file',
        file_name: fileName,
        file_path: filePath,
      }), err);
      setError(`Failed to download file: ${err.message}`);
    }
  };

  const handleClearAll = async () => {
    if (!selectedCounty) return;
    if (window.confirm(`Are you sure you want to delete all ${documents.length} documents for ${selectedCounty}? This action cannot be undone.`)) {
      setIsLoading(true);
      setError('');
      try {
        const countyId = await getCountyId(selectedCounty);
        await apiService.clearCountyDocuments(countyId);
        fetchCountySpecificData();
        fetchCountyStats();
      } catch (err)
       {
        setError('Failed to clear all documents.');
        logger.apiFailure('Failed to clear all documents', countyLogCtx({ action: 'clear_all_documents' }), err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResetProcessing = async () => {
    if (!selectedCounty) return;
    if (window.confirm(`Are you sure you want to RESET processing for ${selectedCounty}? This will DELETE all generated reports and set all documents back to 'unprocessed'.`)) {
      setIsLoading(true);
      setError('');
      try {
        const countyId = await getCountyId(selectedCounty);
        addUserActionBreadcrumb('Reset county processing', countyLogCtx({ action: 'reset_processing' }));
        await apiService.resetCountyProcessing(countyId);
        fetchCountySpecificData();
        fetchCountyStats();
        fetchQueueAndHistory();
      } catch (err) {
        setError('Failed to reset processing.');
        logger.apiFailure('Failed to reset county processing', countyLogCtx({ action: 'reset_processing' }), err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClearPipelineData = async () => {
    if (!selectedCounty) return;
    if (!window.confirm(
      `Clear ALL pipeline data for ${selectedCounty}?\n\nThis will delete:\n• All documents and storage files\n• All scraping tasks\n• All report generation tasks and artifacts\n• All report entries\n\nScraping sources and county config will be preserved.`
    )) return;
    setIsLoading(true);
    setError('');
    try {
      const countyId = await getCountyId(selectedCounty);
      addUserActionBreadcrumb('Clear county pipeline data', countyLogCtx({ action: 'clear_pipeline_data' }));
      const result = await apiService.clearCountyPipelineData(countyId);
      setError('');
      fetchCountySpecificData();
      fetchCountyStats();
      fetchQueueAndHistory();
    } catch (err) {
      setError(`Failed to clear pipeline data: ${err.message}`);
      logger.apiFailure('Failed to clear pipeline data', countyLogCtx({ action: 'clear_pipeline_data' }), err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartGeneration = async (forceRerun = false) => {
    if (!selectedCounty) return;
    setIsGenerating(true);
    setError('');
    try {
      const countyId = await getCountyId(selectedCounty);
      addUserActionBreadcrumb('Queue report generation', countyLogCtx({
        action: 'queue_report_generation',
        force_rerun: forceRerun,
      }));
      await apiService.startCountyReportGeneration(countyId, forceRerun);
      fetchCountySpecificData();
      fetchQueueAndHistory();
      fetchCountyStats();
      if (forceRerun) {
        setShowForceRerunDialog(false);
      }
    } catch (err) {
      setError(`Failed to start report generation. ${err.message}`);
      logger.apiFailure('Failed to start report generation', countyLogCtx({
        action: 'queue_report_generation',
        force_rerun: forceRerun,
      }), err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleForceRerunConfirm = () => {
    handleStartGeneration(true);
  };

  const handleForceRerunCancel = () => {
    setShowForceRerunDialog(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'unprocessed': return 'default';
      case 'queued': return 'warning';
      case 'processing': return 'info';
      case 'processed': return 'success';
      case 'skipped': return 'default';
      case 'compression_failed': return 'warning';
      case 'cancelled': return 'default';
      case 'running': return 'primary';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatProcessingStatusLabel = (status) => {
    if (!status) return 'unprocessed';
    if (status === 'compression_failed') return 'compression failed';
    return status.replace(/_/g, ' ');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const docSortTimestamp = (doc) =>
    new Date(doc.event_date || doc.uploaded_at || doc.created_at || 0).getTime();

  // Newest first (event_date, then upload/created time)
  const sortedDocuments = useMemo(() => {
    return [...documents].sort((a, b) => docSortTimestamp(b) - docSortTimestamp(a));
  }, [documents]);

  // Documents eligible for report generation: approved OR raw (not rejected)
  // With file cleaning disabled for beta, scraped docs land as 'raw' and are directly eligible.
  const eligibleDocs = sortedDocuments.filter(doc => doc.classification_status !== 'rejected');
  const unprocessedEligibleDocs = eligibleDocs.filter(doc => doc.processing_status === 'unprocessed');
  
  // Selection handlers
  const handleToggleDoc = (docId) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const eligibleIds = eligibleDocs.map(d => d.id);
    setSelectedDocIds(new Set(eligibleIds));
  };

  const handleSelectNone = () => {
    setSelectedDocIds(new Set());
  };

  const handleSelectOldestN = (n) => {
    if (!n || n <= 0) return;
    // List is newest-first; tail slice selects the oldest unprocessed docs.
    const oldestUnprocessed = unprocessedEligibleDocs.slice(-n);
    setSelectedDocIds(new Set(oldestUnprocessed.map((d) => d.id)));
  };

  const handleQueueSelected = async () => {
    if (selectedDocIds.size === 0) return;
    setIsGenerating(true);
    setError('');
    try {
      const countyId = await getCountyId(selectedCounty);
      await apiService.startCountyReportGeneration(countyId, false, Array.from(selectedDocIds));
      setSelectedDocIds(new Set());
      fetchCountySpecificData();
      fetchQueueAndHistory();
      fetchCountyStats();
    } catch (err) {
      setError(`Failed to queue selected files. ${err.message}`);
      logger.apiFailure('Failed to queue selected documents', countyLogCtx({
        action: 'queue_selected_documents',
        document_count: selectedDocIds.size,
      }), err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Clear selection when county changes
  useEffect(() => {
    setSelectedDocIds(new Set());
  }, [selectedCounty]);

  // Only approved documents can be processed for reports
  const sortedApprovedDocs = eligibleDocs;
  const unprocessedCount = unprocessedEligibleDocs.length;
  const queuedCount = eligibleDocs.filter(doc => doc.processing_status === 'queued').length;
  const processingCount = eligibleDocs.filter(doc => doc.processing_status === 'processing').length;
  const processedCount = eligibleDocs.filter(doc => doc.processing_status === 'processed').length;
  const skippedCount = eligibleDocs.filter(doc => doc.processing_status === 'skipped').length;
  const compressionFailedCount = eligibleDocs.filter(doc => doc.processing_status === 'compression_failed').length;
  const failedDocCount = eligibleDocs.filter(doc => doc.processing_status === 'failed').length;
  const failedFiles = currentBatch.filter(f => uploadProgress[f.name]?.status === 'Failed');
  
  // Classification stats
  const rawCount = documents.filter(doc => doc.classification_status === 'raw').length;
  const approvedCount = documents.filter(doc => doc.classification_status === 'approved').length;
  const eligibleCount = eligibleDocs.length;
  const rejectedCount = documents.filter(doc => doc.classification_status === 'rejected').length;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        File and Report Management
      </Typography>
      
      <QueueManager tasks={queueTasks} onQueueUpdate={fetchQueueAndHistory} />
      <TaskHistory tasks={historyTasks} onRefresh={fetchQueueAndHistory} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FormControl fullWidth>
          <InputLabel>County</InputLabel>
          <Select
            value={selectedCounty}
            label="County"
            onChange={(e) => setSelectedCounty(e.target.value)}
            disabled={isLoading || displayCounties.length === 0}
            renderValue={(selected) => selected}
            MenuProps={{
              style: {
                zIndex: 1601,
              },
            }}
          >
            {displayCounties.map((county) => (
              <MenuItem key={county.name} value={county.full_name}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body1">{county.full_name}</Typography>
                  <Box>
                    <StatusChip label="Processed" count={county.processed_count} color="success" />
                    <StatusChip label="Queued" count={county.queued_count} color="warning" />
                    <StatusChip label="Processing" count={county.processing_count} color="info" />
                    <StatusChip label="Unprocessed" count={county.unprocessed_count} color="default" />
                    <StatusChip label="Failed" count={county.failed_count} color="error" />
                    {county.skipped_count > 0 && (
                      <StatusChip label="Skipped" count={county.skipped_count} color="default" />
                    )}
                    {county.compression_failed_count > 0 && (
                      <StatusChip label="Compress fail" count={county.compression_failed_count} color="warning" />
                    )}
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            fetchCountyStats();
            if (selectedCounty) fetchCountySpecificData();
            fetchQueueAndHistory();
          }}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {selectedCounty && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Upload Files</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              
              {currentBatch.length === 0 ? (
                <>
                  <Button
                    variant="contained"
                    component="label"
                    endIcon={<UploadFile />}
                    disabled={isUploading || isLoading}
                  >
                    Select Files
                    <input id="file-upload-input" type="file" hidden onChange={handleFileChange} accept=".pdf" multiple />
                  </Button>
                  <Button onClick={handleUpload} disabled={selectedFiles.length === 0 || isUploading || isLoading}>
                    {`Upload ${selectedFiles.length > 0 ? selectedFiles.length + ' File(s)' : ''}`}
                  </Button>
                </>
              ) : !isUploading ? (
                <>
                  <Button onClick={handleRetryFailed} variant="contained" color="warning" disabled={failedFiles.length === 0}>
                    {`Retry Failed (${failedFiles.length})`}
                  </Button>
                  <Button onClick={handleClearBatch} variant="outlined">
                    Clear Batch
                  </Button>
                </>
              ) : null}

              {isUploading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2">Uploading...</Typography>
                  </Box>
              )}

            </Box>

            {currentBatch.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }} onClick={() => setIsFileViewerExpanded(!isFileViewerExpanded)}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {isUploading ? 'Overall Progress' : 'Upload Complete'} ({`${completedCount} / ${totalFiles} files`})
                  </Typography>
                  <IconButton size="small">
                    {isFileViewerExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <LinearProgress variant="determinate" value={overallProgress} sx={{ height: 8, borderRadius: 4 }}/>
              </Box>
            )}

            <Collapse in={isFileViewerExpanded}>
              <Box sx={{ mb: 2, maxHeight: 300, overflow: 'auto' }}>
                <List dense>
                  {(currentBatch.length > 0 ? currentBatch : selectedFiles).map((file) => {
                    const progress = uploadProgress[file.name];
                    return (
                      <ListItem
                        key={file.name}
                        secondaryAction={
                          <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveSelectedFile(currentBatch.indexOf(file))} disabled={isUploading}>
                            <Delete />
                          </IconButton>
                        }
                      >
                        <ListItemText 
                          primary={file.name} 
                          secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`} 
                        />
                        {progress && (
                          <Box sx={{ width: '50%', ml: 2 }}>
                            <LinearProgress variant="determinate" value={progress.progress || 0} color={progress.status === 'Failed' ? 'error' : 'primary'} />
                            <Typography variant="caption" color={progress.status === 'Failed' ? 'error' : 'text.secondary'}>
                              {progress.status === 'Failed' ? progress.error : progress.status}
                            </Typography>
                          </Box>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Collapse>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">Report Generation</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`${eligibleCount} Eligible`} color="primary" size="small" />
                {rejectedCount > 0 && <Chip label={`${rejectedCount} Rejected`} color="error" size="small" variant="outlined" />}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Chip label={`${unprocessedCount} Unprocessed`} color="default" size="small" />
                <Chip label={`${queuedCount} Queued`} color="warning" size="small" />
                {processingCount > 0 && <Chip label={`${processingCount} Processing`} color="info" size="small" />}
                <Chip label={`${processedCount} Processed`} color="success" size="small" />
                {skippedCount > 0 && <Chip label={`${skippedCount} Skipped`} color="default" size="small" variant="outlined" />}
                {compressionFailedCount > 0 && (
                  <Chip label={`${compressionFailedCount} Compression failed`} color="warning" size="small" variant="outlined" />
                )}
                {failedDocCount > 0 && <Chip label={`${failedDocCount} Failed`} color="error" size="small" />}
              </Box>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All scraped and uploaded documents are eligible for report generation. Queue unprocessed files or select specific ones below.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<PlaylistAdd />}
                onClick={() => handleStartGeneration(false)}
                disabled={isGenerating || unprocessedCount === 0}
                size="small"
              >
                {isGenerating ? <CircularProgress size={20} /> : `Queue ${unprocessedCount} Unprocessed`}
              </Button>
              
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PlaylistAdd />}
                onClick={handleQueueSelected}
                disabled={isGenerating || selectedDocIds.size === 0}
                size="small"
              >
                {isGenerating ? <CircularProgress size={20} /> : `Queue ${selectedDocIds.size} Selected`}
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  type="text"
                  inputMode="numeric"
                  size="small"
                  value={quickSelectInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setQuickSelectInput('');
                      return;
                    }
                    if (/^\d+$/.test(val)) {
                      setQuickSelectInput(val);
                    }
                  }}
                  sx={{ width: 70 }}
                  inputProps={{ min: 0, max: unprocessedCount }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleSelectOldestN(quickSelectCount)}
                  disabled={unprocessedCount === 0 || quickSelectCount === 0}
                >
                  Select Oldest {quickSelectCount || '…'} Unprocessed
                </Button>
              </Box>
              
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Warning />}
                onClick={() => setShowForceRerunDialog(true)}
                disabled={isGenerating || eligibleCount === 0}
                size="small"
              >
                Re-Queue All
              </Button>

              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweep />}
                onClick={handleResetProcessing}
                disabled={isGenerating || isLoading || documents.length === 0}
                size="small"
              >
                Reset County
              </Button>

              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweep />}
                onClick={handleClearPipelineData}
                disabled={isGenerating || isLoading}
                size="small"
              >
                Clear All Data
              </Button>
            </Box>

            {queueTasks.some(t => t.status === 'running' || t.status === 'processing') && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Report generation is now running. Files are being processed...
              </Alert>
            )}

            {unprocessedCount === 0 && eligibleCount > 0 && !queueTasks.some(t => t.status === 'running' || t.status === 'processing' || t.status === 'queued') && (
              <Alert severity="success" sx={{ mb: 2 }}>
                All approved files have been processed.
              </Alert>
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setIsConfigExpanded(!isConfigExpanded)}
            >
              <Box>
                <Typography variant="subtitle1">Report Generation — Default Config</Typography>
                <Typography variant="body2" color="text.secondary">
                  Global defaults applied to every run. A task's own config (if set) layers on top.
                </Typography>
              </Box>
              <IconButton size="small">{isConfigExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
            </Box>
            <Collapse in={isConfigExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 2 }}>
                <ReportGenConfigEditor />
              </Box>
            </Collapse>
          </Paper>

          <Paper sx={{p: 2, mb: 2}}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => {
              const expanding = !isDocumentsExpanded;
              setIsDocumentsExpanded(expanding);
              if (expanding && selectedCounty) {
                fetchCountySpecificData({ silent: true });
              }
            }}>
              <Typography variant="h6">Documents ({documents.length})</Typography>
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                  <Button 
                    color="error" 
                    onClick={(e) => { e.stopPropagation(); handleClearAll(); }}
                    disabled={isLoading || documents.length === 0}
                    startIcon={<Delete />}
                    size="small"
                  >
                    Clear All
                  </Button>
                  <IconButton size="small">
                      {isDocumentsExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
              </Box>
            </Box>

            <Collapse in={isDocumentsExpanded} unmountOnExit>
              {/* Selection controls */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <Button size="small" onClick={handleSelectAll} disabled={eligibleCount === 0}>
                  Select All Eligible ({eligibleCount})
                </Button>
                <Button size="small" onClick={handleSelectNone} disabled={selectedDocIds.size === 0}>
                  Clear Selection
                </Button>
                {selectedDocIds.size > 0 && (
                  <Typography variant="body2" color="primary" sx={{ ml: 1 }}>
                    {selectedDocIds.size} selected
                  </Typography>
                )}
              </Box>
              
              {isLoading && <LinearProgress sx={{ mb: 0.5 }} />}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ width: 40 }}>
                        <Checkbox
                          indeterminate={selectedDocIds.size > 0 && selectedDocIds.size < eligibleCount}
                          checked={eligibleCount > 0 && selectedDocIds.size === eligibleCount}
                          onChange={(e) => e.target.checked ? handleSelectAll() : handleSelectNone()}
                          disabled={eligibleCount === 0}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>File Name</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Classification</TableCell>
                      <TableCell>Reason/Details</TableCell>
                      <TableCell>Processing</TableCell>
                      <TableCell>Event Date</TableCell>
                      <TableCell>Uploaded</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ opacity: isLoading ? 0.4 : 1, transition: 'opacity 0.2s' }}>
                    {sortedDocuments.length > 0 ? sortedDocuments.map((doc) => {
                      const isApproved = doc.classification_status === 'approved';
                      const isRejected = doc.classification_status === 'rejected';
                      const isSelected = selectedDocIds.has(doc.id);
                      return (
                        <TableRow 
                          key={doc.id}
                          selected={isSelected}
                          sx={{ 
                            bgcolor: isSelected ? 'action.selected' : 'inherit',
                            '&:hover': { bgcolor: !isRejected ? 'action.hover' : 'inherit' }
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleToggleDoc(doc.id)}
                              disabled={isRejected}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {doc.file_name}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                            {doc.file_size ? (doc.file_size >= 1048576 ? `${(doc.file_size / 1048576).toFixed(1)} MB` : `${Math.round(doc.file_size / 1024)} KB`) : '—'}
                          </TableCell>
                          <TableCell>{doc.source_system || '—'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={doc.classification_status || 'raw'} 
                              color={doc.classification_status === 'approved' ? 'success' : doc.classification_status === 'rejected' ? 'error' : 'default'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {doc.classification_message || '—'}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={formatProcessingStatusLabel(doc.processing_status)} 
                              color={getStatusColor(doc.processing_status || 'unprocessed')} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>{doc.event_date || '—'}</TableCell>
                          <TableCell>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleDownload(doc.file_path, doc.file_name)}><Download /></IconButton>
                            <IconButton size="small" onClick={() => handleDelete(doc.id)}><Delete /></IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          No documents found for this county.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        </>
      )}

      {/* Force Rerun Confirmation Dialog */}
      <Dialog
        open={showForceRerunDialog}
        onClose={handleForceRerunCancel}
        aria-labelledby="force-rerun-dialog-title"
        aria-describedby="force-rerun-dialog-description"
        sx={{ zIndex: 1602 }}
      >
        <DialogTitle id="force-rerun-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Confirm Re-Queue All Documents
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="force-rerun-dialog-description">
            <strong>Warning:</strong> This will reprocess all {eligibleCount} eligible documents for {selectedCounty}, including those already processed.
            <br /><br />
            <strong>This action will:</strong>
            <br />• Overwrite existing reports in the database
            <br />• Reset all eligible documents to "queued"
            <br />• Take approximately {Math.ceil(eligibleCount / 10)} minutes to complete
            <br /><br />
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleForceRerunCancel} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleForceRerunConfirm} 
            color="warning" 
            variant="contained"
            disabled={isGenerating}
          >
            {isGenerating ? <CircularProgress size={20} /> : 'Yes, Re-Queue All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileAndReportManager;